import {
  Box,
  Paper,
  Typography,
  useTheme,
  Link,
  Skeleton,
  Tooltip
} from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useTranslation } from 'react-i18next';
import { ResultChip } from '@components/ResultChip';
import { StatusChip } from '@components/StatusChip';
import { ResponsiveStatusDisplay } from '@components/ResponsiveStatusDisplay';
import { useEquipmentPhotosWithFullContent } from '../hooks/useEquipmentPhotosWithFullContent';
import { EQUIPMENT_STATUS } from '../utils';
import type { EquipmentReviewData, EquipmentStatus } from '../types';

type Props = {
  item: EquipmentReviewData;
  onAssetClick: (woDetailGlobalId: string) => void;
};

const EVENT_STATUS_TRANSLATION_KEYS: Record<EquipmentStatus, string> = {
  [EQUIPMENT_STATUS.Complete]: 'complete',
  [EQUIPMENT_STATUS.InProgress]: 'inProgress',
  [EQUIPMENT_STATUS.NotStarted]: 'notStarted'
};

const getEventStatusLabel = (
  status: EquipmentStatus,
  t: (key: string) => string
): string => {
  return t(EVENT_STATUS_TRANSLATION_KEYS[status]);
};

export const EquipmentReviewCard = ({ item, onAssetClick }: Props) => {
  const { t } = useTranslation('customerReview');
  const theme = useTheme();

  const {
    coverPhoto,
    coverPhotoIsUnsynced,
    promotedPhotos: fullPromotedPhotos,
    isLoading: isLoadingPhotos
  } = useEquipmentPhotosWithFullContent({
    eventNum: item.eventNum,
    coverPhotoIndex: item.coverPhotoIndex,
    promotedPhotoIndices: item.promotedPhotoIndices,
    promotedPhotos: item.promotedPhotos,
    fallbackCoverUrl: item.coverImageUrl,
    coverPhotoIsUnsynced: item.coverPhotoIsUnsynced
  });

  const hasImage = !!coverPhoto || isLoadingPhotos;
  const isEventIncomplete = item.eventStatus !== EQUIPMENT_STATUS.Complete;

  const handleAssetClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (item.woDetailGlobalId) {
      onAssetClick(item.woDetailGlobalId);
    }
  };

  const statusItems = [
    {
      label: t('workStatus'),
      value: item.workStatus,
      chipVariant: 'status' as const,
      chipSize: 'small' as const
    },
    {
      label: t('eventStatus'),
      value: getEventStatusLabel(item.eventStatus, t),
      chipVariant: 'status' as const,
      chipSize: 'small' as const
    }
  ];

  const renderStatusOrResult = (
    result: 'pass' | 'fail' | null,
    label: string
  ) => {
    if (result !== null) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label}:
          </Typography>
          <ResultChip
            result={result === 'pass' ? 'Pass' : 'Fail'}
            size="small"
            showIcon
          />
        </Box>
      );
    }

    if (isEventIncomplete) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {label}:
          </Typography>
          <StatusChip
            label={getEventStatusLabel(item.eventStatus, t)}
            variant="status"
            size="small"
          />
        </Box>
      );
    }

    return null;
  };

  const displayTitle =
    [item.assetId, item.manufacturer, item.modelNumber, item.description]
      .filter(Boolean)
      .join(' - ') || 'No Asset ID';

  return (
    <Paper
      elevation={1}
      sx={{
        p: 2,
        mb: 1.5,
        borderRadius: 2
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: hasImage ? 'row' : 'column'
        }}
      >
        {hasImage && (
          <Box
            sx={{
              width: 80,
              height: 80,
              flexShrink: 0,
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: theme.palette.action.hover,
              position: 'relative'
            }}
          >
            {isLoadingPhotos ? (
              <Skeleton variant="rectangular" width={80} height={80} />
            ) : (
              <>
                <Box
                  component="img"
                  src={coverPhoto || ''}
                  alt={`${item.assetId} cover`}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                {coverPhotoIsUnsynced && (
                  <Tooltip title={t('photoNotSynced')} placement="top">
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 2,
                        right: 2,
                        backgroundColor: 'rgba(255, 152, 0, 0.9)',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CloudSyncIcon sx={{ fontSize: 14, color: 'white' }} />
                    </Box>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1
            }}
          >
            <Link
              component="button"
              variant="subtitle2"
              onClick={handleAssetClick}
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                textDecoration: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {displayTitle}
            </Link>
            <ResponsiveStatusDisplay
              items={statusItems}
              mobileLayout="horizontal"
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('testsPerformed', { count: item.testsCompleted })}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
            {renderStatusOrResult(item.asFoundResult, t('asFound'))}
            {renderStatusOrResult(item.asLeftResult, t('asLeft'))}
          </Box>

          {item.inspectionNotes && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                {t('technicianNotes')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.inspectionNotes}
              </Typography>
            </Box>
          )}

          {(fullPromotedPhotos.length > 0 ||
            (isLoadingPhotos && item.promotedPhotos.length > 0)) && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                backgroundColor: 'action.hover',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="primary.main"
                sx={{ mb: 0.5 }}
              >
                {t('photosForReport')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5 }}
              >
                {t('photosForReportDescription')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {isLoadingPhotos
                  ? item.promotedPhotos.map((_, index) => (
                      <Skeleton
                        key={`skeleton-${index}`}
                        variant="rectangular"
                        width={80}
                        height={80}
                        sx={{ borderRadius: 1 }}
                      />
                    ))
                  : fullPromotedPhotos.map(photo => (
                      <Box key={photo.id} sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{ position: 'relative', display: 'inline-block' }}
                        >
                          <Box
                            component="img"
                            src={photo.fullDataUrl}
                            alt={photo.caption || 'Promoted photo'}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1,
                              border: '2px solid',
                              borderColor: 'primary.main'
                            }}
                          />
                          {photo.isUnsynced && (
                            <Tooltip
                              title={t('photoNotSynced')}
                              placement="top"
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 2,
                                  right: 2,
                                  backgroundColor: 'rgba(255, 152, 0, 0.9)',
                                  borderRadius: '50%',
                                  width: 20,
                                  height: 20,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <CloudSyncIcon
                                  sx={{ fontSize: 14, color: 'white' }}
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Box>
                        {photo.caption && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              maxWidth: 80,
                              mt: 0.5,
                              wordBreak: 'break-word'
                            }}
                          >
                            {photo.caption}
                          </Typography>
                        )}
                      </Box>
                    ))}
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('condition')} ({t('asFound')})
                </Typography>
                <Typography variant="body2">
                  {item.conditionAsFound || t('notSet')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('condition')} ({t('asLeft')})
                </Typography>
                <Typography variant="body2">
                  {item.conditionAsLeft || t('notSet')}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('operability')} ({t('asFound')})
                </Typography>
                <Typography variant="body2">
                  {item.operabilityAsFound || t('notSet')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('operability')} ({t('asLeft')})
                </Typography>
                <Typography variant="body2">
                  {item.operabilityAsLeft || t('notSet')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};
